import { FC, memo } from 'react';
import { Input, Button, Skeleton } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import Blocks from '../Blocks/Blocks';
import SiteMenu from '../SiteMenu/SiteMenu';
import { CallPoolSettingsTable } from './CallPoolSettingsTable/CallPoolSettingsTable';
import { CreateCallPoolModal } from '../../features/callpool/CreateCallPoolModal';
import { useCallPoolSettings } from '../../hooks/components/useCallPoolSettings/useCallPoolSettings';
import styles from './styles.module.css';
import blockStyles from '../Blocks/style.module.css';

const SKELETON_CARD_COUNT = 6;
const SEARCH_TOOLTIP = 'Поиск по наименованию пула или клинике';

const CallPoolSkeleton: FC = () => (
  <div className={styles.skeletonList}>
    {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
      <div key={i} className={styles.skeletonCard}>
        <Skeleton.Avatar active shape="square" size={44} />
        <Skeleton
          active
          title={{ width: '40%' }}
          paragraph={{ rows: 1, width: ['60%'] }}
          className={styles.skeletonText}
        />
      </div>
    ))}
  </div>
);

export const CallPoolSettings: FC = memo(() => {
  const props = useCallPoolSettings();

  return (
    <>
      <Blocks
        topBlock={
          <div className={`${blockStyles.topBlockRow} ${styles.headerRow}`}>
            <SiteMenu
              title="Настройки: Пулы обзвона"
              subtitle="Управление пулами системы обзвона"
              svg={true}
              svgType="callPoolSettings"
            />

            <div className={styles.headerControls}>
              <Input
                className={styles.search}
                placeholder={SEARCH_TOOLTIP}
                title={SEARCH_TOOLTIP}
                aria-label={SEARCH_TOOLTIP}
                prefix={<SearchOutlined className={styles.searchIcon} />}
                value={props.searchValue}
                onChange={(e) => props.handleSearchChange(e.target.value)}
                allowClear
                disabled={props.status.responseLoading || props.saving}
              />
              <Button
                className={styles.addButton}
                type="primary"
                icon={<PlusOutlined />}
                onClick={props.handleAddRow}
                disabled={
                  props.editModalOpen ||
                  props.status.responseLoading ||
                  props.saving
                }
              >
                Добавить пул
              </Button>
            </div>
          </div>
        }
        mainBlock={
          props.status.responseLoading && props.items.length === 0 ? (
            <CallPoolSkeleton />
          ) : (
            <CallPoolSettingsTable
              items={props.items}
              totalItemsCount={props.totalItemsCount}
              pageIndex={props.pageIndex}
              pageSize={props.pageSize}
              loading={props.status.responseLoading}
              hasLoadError={Boolean(props.status.responseError)}
              onPageChange={props.handlePageChange}
              onPageSizeChange={props.handlePageSizeChange}
              onStartEdit={props.handleStartEdit}
              poolClinicsLoading={props.poolClinicsLoading}
              actionsDisabled={props.status.responseLoading || props.saving}
            />
          )
        }
      />

      <CreateCallPoolModal
        open={props.createModalOpen}
        saving={props.saving}
        clinics={props.clinics}
        onClose={props.handleCloseCreateModal}
        onSubmit={props.handleCreateCallPool}
      />
      <CreateCallPoolModal
        open={props.editModalOpen}
        mode="edit"
        saving={props.saving}
        clinics={props.clinics}
        initialValues={props.editModalInitialValues}
        onClose={props.handleCancelEdit}
        onSubmit={props.handleUpdateCallPool}
      />
    </>
  );
});

CallPoolSettings.displayName = 'CallPoolSettings';
